import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Save, RotateCcw, Lock, AlertTriangle, Euro, Plus, Trash2, X } from 'lucide-react';

const emptyMotifForm = { code: '', libelle: '', taux: '', soumis_ratio: false };

const AdminTarifsReduitsTICGN = () => {
  const { hasPermission } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingCode, setSavingCode] = useState(null);
  const [draftMotifs, setDraftMotifs] = useState({}); // { code: { libelle, taux, soumis_ratio } }
  const [showAddModal, setShowAddModal] = useState(false);
  const [newMotif, setNewMotif] = useState(emptyMotifForm);
  const [creating, setCreating] = useState(false);

  const canEdit = hasPermission && hasPermission('admin_tarifs', 'edit');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/tarifs-reduits-ticgn');
      setData(data);
      // Initialise les drafts à partir des motifs courants
      const drafts = {};
      Object.entries(data.motifs || {}).forEach(([code, m]) => {
        drafts[code] = {
          libelle: m.libelle ?? '',
          taux: m.taux ?? 0,
          soumis_ratio: !!m.soumis_ratio,
        };
      });
      setDraftMotifs(drafts);
    } catch (e) {
      toast.error('Erreur chargement des tarifs réduits');
    } finally {
      setLoading(false);
    }
  };

  const isDirty = (code) => {
    if (!data || !data.motifs?.[code]) return false;
    const m = data.motifs[code];
    const d = draftMotifs[code];
    if (!d) return false;
    return (
      String(d.libelle ?? '') !== String(m.libelle ?? '') ||
      Number(d.taux) !== Number(m.taux) ||
      Boolean(d.soumis_ratio) !== Boolean(m.soumis_ratio)
    );
  };

  const handleSaveMotif = async (code) => {
    const d = draftMotifs[code];
    if (!d) return;
    if (!d.libelle?.trim()) { toast.error('Libellé obligatoire'); return; }
    const v = Number(d.taux);
    if (isNaN(v) || v < 0) { toast.error('Tarif invalide (≥ 0)'); return; }
    setSavingCode(code);
    try {
      const { data: result } = await api.put(`/admin/tarifs-reduits-ticgn/motifs/${code}`, {
        libelle: d.libelle.trim(),
        taux: v,
        soumis_ratio: !!d.soumis_ratio,
      });
      setData(result);
      const drafts = { ...draftMotifs };
      drafts[code] = {
        libelle: result.motifs[code].libelle,
        taux: result.motifs[code].taux,
        soumis_ratio: !!result.motifs[code].soumis_ratio,
      };
      setDraftMotifs(drafts);
      toast.success(`Motif ${code} sauvegardé`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setSavingCode(null);
    }
  };

  const handleDeleteMotif = async (code) => {
    if (!window.confirm(`Supprimer définitivement le motif "${code}" ?`)) return;
    try {
      const { data: result } = await api.delete(`/admin/tarifs-reduits-ticgn/motifs/${code}`);
      setData(result);
      const drafts = { ...draftMotifs };
      delete drafts[code];
      setDraftMotifs(drafts);
      toast.success('Motif supprimé');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression');
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Réinitialiser tous les motifs aux valeurs par défaut ? Les motifs personnalisés seront supprimés.')) return;
    try {
      const { data: result } = await api.post('/admin/tarifs-reduits-ticgn/reset');
      setData(result);
      const drafts = {};
      Object.entries(result.motifs || {}).forEach(([code, m]) => {
        drafts[code] = { libelle: m.libelle, taux: m.taux, soumis_ratio: !!m.soumis_ratio };
      });
      setDraftMotifs(drafts);
      toast.success('Tarifs réinitialisés');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur reset');
    }
  };

  const handleCreateMotif = async (e) => {
    e?.preventDefault?.();
    const code = (newMotif.code || '').trim().toUpperCase();
    const libelle = (newMotif.libelle || '').trim();
    const taux = Number(newMotif.taux);
    if (!/^[A-Z][A-Z0-9_]{1,49}$/.test(code)) {
      toast.error('Code invalide (lettres majuscules / chiffres / _, commence par une lettre, 2-50 caractères)');
      return;
    }
    if (!libelle) { toast.error('Libellé obligatoire'); return; }
    if (isNaN(taux) || taux < 0) { toast.error('Tarif invalide (≥ 0)'); return; }
    setCreating(true);
    try {
      const { data: result } = await api.post('/admin/tarifs-reduits-ticgn/motifs', {
        code, libelle, taux, soumis_ratio: !!newMotif.soumis_ratio,
      });
      setData(result);
      const drafts = { ...draftMotifs };
      drafts[code] = { libelle, taux, soumis_ratio: !!newMotif.soumis_ratio };
      setDraftMotifs(drafts);
      toast.success(`Motif ${code} créé`);
      setShowAddModal(false);
      setNewMotif(emptyMotifForm);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur création');
    } finally {
      setCreating(false);
    }
  };

  if (hasPermission && !hasPermission('admin_tarifs', 'view')) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="admin-tarifs-reduits-denied">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-3">
          <Lock className="w-6 h-6 text-red-600" />
          <div>
            <h2 className="font-semibold text-red-900">Accès refusé</h2>
            <p className="text-sm text-red-800 mt-1">Permission <code>admin_tarifs.view</code> requise.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <div className="p-8 text-sm text-gray-500" data-testid="loading">Chargement…</div>;
  if (!data) return <div className="p-8 text-sm text-red-600">Impossible de charger les tarifs réduits.</div>;

  const motifs = data.motifs || {};

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto space-y-6" data-testid="admin-tarifs-reduits-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Euro className="w-6 h-6 text-blue-600" />
            Tarifs réduits TICGN
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Taux optimisés appliqués pour chaque motif lors du calcul d'un rapport TICGN.
          </p>
          {data.updated_at && (
            <p className="text-xs text-gray-400 mt-1" data-testid="last-updated">
              Dernière mise à jour : {new Date(data.updated_at).toLocaleString('fr-FR')}
            </p>
          )}
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-1 text-sm px-3 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700"
              data-testid="btn-add-motif"
            >
              <Plus className="w-4 h-4" /> Ajouter un motif
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex items-center gap-1 text-sm px-3 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              data-testid="btn-reset"
            >
              <RotateCcw className="w-4 h-4" /> Réinitialiser
            </button>
          </div>
        )}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2 text-xs text-amber-900" data-testid="notice">
        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          Toute modification s'applique <strong>immédiatement</strong> aux nouveaux calculs de rapports TICGN.
          Tous les motifs peuvent être ajoutés, modifiés ou supprimés librement.
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">Code / Libellé</th>
              <th className="px-3 py-2 text-right w-32">Tarif (€/MWh) *</th>
              <th className="px-3 py-2 text-center w-28">Soumis ratio</th>
              <th className="px-3 py-2 text-right w-44">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100" data-testid="tbody-tarifs">
            {Object.entries(motifs).map(([code, m]) => {
              const d = draftMotifs[code] || { libelle: m.libelle, taux: m.taux, soumis_ratio: !!m.soumis_ratio };
              const dirty = isDirty(code);
              return (
                <tr key={code} data-testid={`row-${code}`}>
                  <td className="px-3 py-2 align-top">
                    <div className="text-xs text-gray-500 font-mono">{code}</div>
                    {canEdit ? (
                      <input
                        type="text"
                        value={d.libelle}
                        onChange={e => setDraftMotifs({ ...draftMotifs, [code]: { ...d, libelle: e.target.value } })}
                        className="mt-1 w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        data-testid={`input-libelle-${code}`}
                      />
                    ) : (
                      <div className="mt-1 font-medium text-gray-900">{m.libelle}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right align-top">
                    {canEdit ? (
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={d.taux ?? ''}
                        onChange={e => setDraftMotifs({ ...draftMotifs, [code]: { ...d, taux: e.target.value } })}
                        className="w-28 px-2 py-1 text-sm border border-gray-300 rounded text-right font-mono"
                        data-testid={`input-taux-${code}`}
                      />
                    ) : (
                      <span className="font-mono">{Number(m.taux).toFixed(2).replace('.', ',')}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center align-top">
                    {canEdit ? (
                      <input
                        type="checkbox"
                        checked={!!d.soumis_ratio}
                        onChange={e => setDraftMotifs({ ...draftMotifs, [code]: { ...d, soumis_ratio: e.target.checked } })}
                        className="w-4 h-4"
                        data-testid={`input-ratio-${code}`}
                      />
                    ) : (
                      m.soumis_ratio
                        ? <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">≥ 0,5 % VAC</span>
                        : <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">Sans ratio</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right align-top">
                    {canEdit && (
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => handleSaveMotif(code)}
                          disabled={!dirty || savingCode === code}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40"
                          data-testid={`btn-save-${code}`}
                        >
                          <Save className="w-3 h-3" /> {savingCode === code ? '…' : 'Enregistrer'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteMotif(code)}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-red-50 text-red-700 border border-red-200 rounded hover:bg-red-100"
                          data-testid={`btn-delete-${code}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-500">
        * Les taux EXONÉRATION restent fixes à 0 €/MWh par définition réglementaire. Seuls les tarifs
        réduits (au-delà de l'exonération) sont paramétrables.
      </p>

      {/* Modal d'ajout */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="add-motif-modal">
          <form onSubmit={handleCreateMotif} className="bg-white rounded-xl shadow-lg w-full max-w-md mx-4 p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Plus className="w-5 h-5 text-emerald-600" /> Nouveau motif TICGN
              </h2>
              <button
                type="button"
                onClick={() => { setShowAddModal(false); setNewMotif(emptyMotifForm); }}
                className="p-1 text-gray-400 hover:text-gray-700"
                data-testid="btn-close-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Code (UPPERCASE) *</label>
              <input
                type="text"
                value={newMotif.code}
                onChange={e => setNewMotif({ ...newMotif, code: e.target.value.toUpperCase() })}
                placeholder="ex. NOUVEAU_MOTIF_2026"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded font-mono"
                data-testid="new-code"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Lettres majuscules, chiffres et underscores. 2 à 50 caractères. Commence par une lettre.
              </p>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Libellé *</label>
              <input
                type="text"
                value={newMotif.libelle}
                onChange={e => setNewMotif({ ...newMotif, libelle: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                data-testid="new-libelle"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Tarif (€/MWh) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newMotif.taux}
                  onChange={e => setNewMotif({ ...newMotif, taux: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded font-mono text-right"
                  data-testid="new-taux"
                />
              </div>
              <div className="flex items-center gap-2 pt-5">
                <input
                  id="new-soumis-ratio"
                  type="checkbox"
                  checked={!!newMotif.soumis_ratio}
                  onChange={e => setNewMotif({ ...newMotif, soumis_ratio: e.target.checked })}
                  data-testid="new-soumis-ratio"
                />
                <label htmlFor="new-soumis-ratio" className="text-xs text-gray-700">Soumis au ratio (≥ 0,5 % VAC)</label>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => { setShowAddModal(false); setNewMotif(emptyMotifForm); }}
                className="px-3 py-1.5 text-sm bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                data-testid="btn-cancel-create"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={creating}
                className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
                data-testid="btn-confirm-create"
              >
                {creating ? 'Création…' : 'Créer le motif'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminTarifsReduitsTICGN;
